import React, { useEffect, useState } from "react";
import { 
  FaShieldAlt, 
  FaCheckCircle, 
  FaTimesCircle, 
  FaLock,
  FaQrcode,
  FaBarcode,
  FaInfoCircle,
  FaArrowRight
} from "react-icons/fa";
import { HiOutlineKey, HiOutlineShieldCheck } from "react-icons/hi";
import { BsShieldLock, BsShieldCheck } from "react-icons/bs";

const AuthenticateCode = () => {
  const [code, setCode] = useState("");
  const [validCodes, setValidCodes] = useState([]);
  const [status, setStatus] = useState(""); // success | error | ""
  const [isLoading, setIsLoading] = useState(false);
  const [recentCodes, setRecentCodes] = useState([]);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    loadAuthCodes();
    loadRecentCodes();
  }, []);

  const loadAuthCodes = () => {
    fetch("/assets/auth-codes.txt")
      .then((res) => res.text())
      .then((text) => {
        const codesArray = text
          .replace(/"/g, "")
          .split(",")
          .map((c) => c.trim().toUpperCase())
          .filter(Boolean);

        setValidCodes(codesArray);
      })
      .catch((err) => {
        console.error("Failed to load auth codes", err);
      });
  };

  const loadRecentCodes = () => {
    // Simulate recent verifications - replace with actual data
    const mockRecent = [
      { code: "DRAG-ATP-2024-001", date: "2024-01-15", status: "valid" },
      { code: "DRAG-DECA-2024-089", date: "2024-01-14", status: "valid" },
      { code: "DRAG-NPP-2024-156", date: "2024-01-13", status: "invalid" },
    ];
    setRecentCodes(mockRecent);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);

    if (!code.trim()) {
      alert("Please enter your authentication code");
      setIsLoading(false);
      return;
    }

    const enteredCode = code.trim().toUpperCase();

    // Simulate API delay
    setTimeout(() => {
      if (validCodes.includes(enteredCode)) {
        setStatus("success");
        // Add to recent verifications
        setRecentCodes(prev => [
          { code: enteredCode, date: new Date().toISOString().split('T')[0], status: "valid" },
          ...prev.slice(0, 2)
        ]);
      } else {
        setStatus("error");
      }
      setIsLoading(false);
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white overflow-hidden">
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white py-20 md:py-28 overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-red-600/20 to-amber-600/20 rounded-full blur-3xl animate-pulse-slow"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-blue-600/20 to-purple-600/20 rounded-full blur-3xl animate-pulse-slow animation-delay-2000"></div>
          
          {/* Grid Pattern */}
            <div
              className="absolute inset-0 opacity-20"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              }}
            ></div>        
         </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10">
          <div className={`transform transition-all duration-1000 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            {/* Breadcrumb */}
            <div className="flex justify-center items-center gap-2 text-sm text-gray-400 mb-6">
              <span>Home</span>
              <span>•</span>
              <span className="text-red-500">Authenticate Code</span>
            </div>

            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              <span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                Verify Your 
              </span>
              <span className="bg-gradient-to-r from-red-500 to-amber-500 bg-clip-text text-transparent">
                {" "}Product
              </span>
            </h1>
            
            <p className="text-gray-300 max-w-2xl mx-auto text-lg">
              Ensure your product's authenticity by entering the unique code
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-3 gap-8">
          
          {/* Left Column - Information */}
          <div className={`lg:col-span-1 space-y-6 transform transition-all duration-1000 ${
            isVisible ? 'translate-x-0 opacity-100' : '-translate-x-20 opacity-0'
          }`}>
            {/* Security Badge */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
              <div className="w-16 h-16 bg-gradient-to-br from-red-600 to-amber-600 rounded-2xl flex items-center justify-center mb-6">
                <BsShieldLock className="text-white text-3xl" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Secure Verification</h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                Each product comes with a unique authentication code. Enter it below to verify 
                its authenticity and ensure you're getting genuine Dark Drag products.
              </p>
              
              {/* Features List */}
              <div className="space-y-3">
                {[
                  "Unique codes for each product",
                  "Real-time verification",
                  "Protect against counterfeits",
                  "Instant results"
                ].map((feature, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-gray-600">
                    <FaCheckCircle className="text-green-500 text-xs" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* How to Find Code */}
            <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border border-gray-200">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <FaInfoCircle className="text-red-600" />
                Where to find your code?
              </h4>
              <p className="text-sm text-gray-600 mb-3">
                The authentication code is located under the scratch-off panel on your product packaging.
              </p>
              <div className="flex gap-3 text-xs text-gray-500">
                <span className="flex items-center gap-1">
                  <FaBarcode /> Scratch panel
                </span>
                <span className="flex items-center gap-1">
                  <FaQrcode /> Scan QR code
                </span>
              </div>
            </div>
          </div>

          {/* Center Column - Verification Form */}
          <div className={`lg:col-span-1 transform transition-all duration-1000 delay-300 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'
          }`}>
            <div className="bg-white rounded-3xl shadow-2xl p-8 border border-gray-100">
              {/* Form Header */}
              <div className="text-center mb-8">
                <div className="inline-block p-4 bg-gradient-to-br from-red-50 to-amber-50 rounded-full mb-4">
                  <HiOutlineShieldCheck className="text-red-600 text-4xl" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Verify Authenticity</h2>
                <p className="text-gray-500 text-sm">Enter the 16-digit code from your product</p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Code Input */}
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2">
                    <HiOutlineKey className="text-gray-400 text-xl" />
                  </div>
                  <input
                    type="text"
                    value={code}
                    onChange={(e) => {
                      setCode(e.target.value.toUpperCase());
                      setStatus("");
                    }}
                    placeholder="Enter Your Authentication Code..."
                    className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/20 transition-all text-gray-900 font-mono text-sm uppercase"
                    maxLength="20"
                  />
                  
                  {/* Format hint */}
                  <div className="absolute right-4 top-1/2 -translate-y-1/2">
                    <span className="text-xs text-gray-400">XXXX-XXXX-XXXX-XXXX</span>
                  </div>
                </div>

                {/* Status Messages */}
                {status === "success" && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3">
                    <FaCheckCircle className="text-green-500 text-xl flex-shrink-0" />
                    <div>
                      <p className="text-green-700 font-semibold">Product verified successfully!</p>
                      <p className="text-green-600 text-sm mt-1">This is a genuine Dark Drag product.</p>
                    </div>
                  </div>
                )}

                {status === "error" && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
                    <FaTimesCircle className="text-red-500 text-xl flex-shrink-0" />
                    <div>
                      <p className="text-red-700 font-semibold">Invalid authentication code!</p>
                      <p className="text-red-600 text-sm mt-1">Please check the code and try again.</p>
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full group relative px-8 py-4 bg-gradient-to-r from-red-600 to-amber-600 text-white font-semibold rounded-xl hover:shadow-xl hover:shadow-red-600/20 transition-all duration-300 overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Verifying...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      Verify Product
                      <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
                    </span>
                  )}
                </button>

                {/* Trust Badge */}
                <p className="text-xs text-gray-400 text-center flex items-center justify-center gap-1">
                  <FaLock className="text-gray-400" />
                  Secure verification • Your code is encrypted
                </p>
              </form>
            </div>
          </div>

          {/* Right Column - Recent Verifications */}
          <div className={`lg:col-span-1 transform transition-all duration-1000 delay-500 ${
            isVisible ? 'translate-x-0 opacity-100' : 'translate-x-20 opacity-0'
          }`}>
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <FaShieldAlt className="text-red-600" />
                Recent Verifications
              </h3>

              <div className="space-y-4">
                {recentCodes.map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
                    <div>
                      <p className="text-sm font-mono text-gray-900">{item.code}</p>
                      <p className="text-xs text-gray-500">{item.date}</p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      item.status === 'valid' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {item.status === 'valid' ? 'Verified' : 'Invalid'}
                    </span>
                  </div>
                ))}
              </div>

              {/* Stats */}
              <div className="mt-6 pt-6 border-t border-gray-100">
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-gray-900">10k+</p>
                    <p className="text-xs text-gray-500">Products Verified</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">100%</p>
                    <p className="text-xs text-gray-500">Secure</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            {
              icon: BsShieldCheck,
              title: "Anti-Counterfeit",
              desc: "Advanced security features to ensure product authenticity"
            },
            {
              icon: FaQrcode,
              title: "Scan & Verify",
              desc: "Quick verification using QR code scanner"
            },
            {
              icon: FaCheckCircle,
              title: "Instant Results",
              desc: "Get immediate verification results in seconds"
            }
          ].map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div key={index} className="bg-white rounded-xl p-6 border border-gray-100 shadow-lg hover:shadow-xl transition-all duration-300">
                <Icon className="text-red-600 text-3xl mb-4" />
                <h4 className="font-semibold text-gray-900 mb-2">{feature.title}</h4>
                <p className="text-sm text-gray-600">{feature.desc}</p>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default AuthenticateCode;